import { Link } from "react-router-dom";
import { ArrowRight, Shield, Cloud, Cpu, UserCheck, Target, Layers } from "lucide-react";
import Reveal from "@/components/reveal";

const focusAreas = [
  {
    icon: Shield,
    title: "Cybersecurity",
    testId: "focus-card-cybersecurity",
    roles: ["Security Engineering", "Cloud Security", "Application Security", "IAM", "SOC", "GRC", "Security Architecture"],
  },
  {
    icon: Cloud,
    title: "Cloud & Infrastructure",
    testId: "focus-card-cloud-infrastructure",
    roles: ["Cloud Engineering", "Infrastructure Engineering", "Platform Engineering", "DevOps", "Site Reliability Engineering", "Linux & Systems Engineering"],
  },
];

const differentiators = [
  { icon: Cpu, title: "Engineering Background + Recruitment Expertise", desc: "Technical understanding combined with years of specialist recruitment experience." },
  { icon: UserCheck, title: "Founder-Led & Personal", desc: "No unnecessary layers or handovers. Work directly with the person leading your search." },
  { icon: Layers, title: "Quality Over Quantity", desc: "Focused searches and relevant candidates rather than large volumes of CVs." },
  { icon: Target, title: "Targeted Talent Search", desc: "Going beyond job ads to identify and engage the right specialists." },
];

const Home = () => (
  <main data-testid="home-page">
    {/* 1 — HERO (dark) */}
    <section className="relative bg-[#0B132B] overflow-hidden">
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute inset-0 orange-glow" />
      <div className="container-nb relative pt-36 pb-24 md:pt-44 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow mb-6">Specialist Cybersecurity Talent Partner</p>
            <h1 data-testid="hero-headline-text" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-[#F8FAFC] max-w-3xl">
              Tech Talent.<br />
              <span className="text-[#FF5E1A]">Zero Guesswork.</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-xl">
              Boutique technical recruitment for Cybersecurity, Cloud and Software Engineering teams
              across the Netherlands and Europe.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/employers" data-testid="hero-cta-hire-talent" className="btn-primary">
                Hire Specialist Talent <ArrowRight size={15} />
              </Link>
              <Link to="/services" data-testid="hero-cta-explore-services" className="btn-outline-dark">
                Explore Our Services <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 lg:mt-0 lg:col-span-5" data-testid="hero-visual">
            <Reveal delay={0.15}>
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-8" style={{ background: "radial-gradient(ellipse 55% 55% at 60% 40%, rgba(255,94,26,0.15), transparent 70%)" }} />
                <div className="radar-visual" data-testid="hero-radar" aria-hidden="true">
                  <div className="radar-ring" style={{ inset: 0 }} />
                  <div className="radar-ring" style={{ inset: "12.5%" }} />
                  <div className="radar-ring" style={{ inset: "25%" }} />
                  <div className="radar-ring" style={{ inset: "37.5%" }} />
                  <div className="radar-crosshair" style={{ left: 0, right: 0, top: "50%", height: 1 }} />
                  <div className="radar-crosshair" style={{ top: 0, bottom: 0, left: "50%", width: 1 }} />
                  <div className="radar-sweep" />
                  <div className="radar-sweep-line" />
                  <div className="radar-core" />
                  <div className="radar-blip rb1" />
                  <div className="radar-blip rb2" />
                  <div className="radar-blip rb3" />
                  <div className="radar-blip rb4" />
                  <div className="radar-blip rb5" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* 2 — WHY SPECIALIST HIRING IS DIFFERENT (light) */}
    <section className="bg-[#FAF9F5]">
      <div className="container-nb py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
          <Reveal>
            <p className="eyebrow mb-5">Why Specialist Hiring Is Different</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug text-[#0F172A]">
              The best specialists aren't actively applying.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="space-y-6">
            <p className="text-base text-[#475569] leading-relaxed">
              The Cybersecurity and Infrastructure professionals companies want to hire are often already
              employed, busy and selective about who they speak to.
            </p>
            <p className="text-base text-[#475569] leading-relaxed">
              Finding the right person requires more than keyword searches. It requires understanding the
              role, the technology and having meaningful conversations with the people behind the CV.
            </p>
            <div className="border-l-2 border-[#FF5E1A] pl-5 py-1">
              <p className="font-display text-lg md:text-xl font-semibold text-[#0F172A] leading-snug italic">
                The challenge isn't finding more CVs. It's finding the right specialist.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* 3 — OUR FOCUS (dark) */}
    <section className="relative bg-[#0F172A] overflow-hidden">
      <div className="absolute inset-0 grid-overlay" />
      <div className="container-nb relative py-20 md:py-28">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-5">Our Focus</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug text-[#F8FAFC]">
            Focused where technology matters.
          </h2>
          <p className="mt-5 text-base text-[#94A3B8] leading-relaxed">
            NicheBytes specialises in technical domains that build, secure and operate modern digital environments.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {focusAreas.map((area, i) => (
            <Reveal key={area.title} delay={i * 0.1}>
              <div data-testid={area.testId} className="card-dark p-8 md:p-10 h-full transition-colors duration-300 hover:border-[#FF5E1A]/40">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-[#FF5E1A]/12 border border-[#FF5E1A]/25 flex items-center justify-center">
                    <area.icon size={20} className="text-[#FF5E1A]" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-[#F8FAFC]">{area.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {area.roles.map((r) => <span key={r} className="pill-dark">{r}</span>)}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 4 — WHY NICHEBYTES (light) */}
    <section className="bg-[#FAF9F5]">
      <div className="container-nb py-20 md:py-28">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-5">Why NicheBytes</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug text-[#0F172A]">
            Great technical hiring starts with genuine technical understanding.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentiators.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.08}>
              <div className="card-light p-7 h-full transition-transform duration-300 hover:-translate-y-1" data-testid={`why-card-${i + 1}`}>
                <d.icon size={22} className="text-[#FF5E1A] mb-5" />
                <h3 className="font-display text-lg font-semibold text-[#0F172A] leading-snug mb-3">{d.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{d.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 6 — EMPLOYERS & CANDIDATES (light) */}
    <section className="bg-white border-y border-[#E6E2D8]">
      <div className="container-nb py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <Reveal>
            <div className="card-light p-8 md:p-10 h-full flex flex-col" data-testid="gateway-card-employers">
              <p className="font-mono-accent text-xs font-semibold tracking-[0.22em] uppercase text-[#FF5E1A] mb-4">For Employers</p>
              <h3 className="font-display text-xl md:text-2xl font-semibold text-[#0F172A] mb-4">Looking to hire?</h3>
              <p className="text-sm md:text-base text-[#475569] leading-relaxed mb-8 flex-1">
                Find specialist Cybersecurity, Cloud and Infrastructure talent through focused search and
                personal recruitment partnership.
              </p>
              <Link to="/employers" data-testid="gateway-cta-employers" className="btn-outline-light w-fit">
                For Employers <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-light p-8 md:p-10 h-full flex flex-col" data-testid="gateway-card-candidates">
              <p className="font-mono-accent text-xs font-semibold tracking-[0.22em] uppercase text-[#FF5E1A] mb-4">For Candidates</p>
              <h3 className="font-display text-xl md:text-2xl font-semibold text-[#0F172A] mb-4">Looking for your next opportunity?</h3>
              <p className="text-sm md:text-base text-[#475569] leading-relaxed mb-8 flex-1">
                Connect with NicheBytes and explore relevant opportunities across Cybersecurity, Cloud and Infrastructure.
              </p>
              <Link to="/candidates" data-testid="gateway-cta-candidates" className="btn-outline-light w-fit">
                For Candidates <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* 7 — FINAL CTA (dark + orange gradient) */}
    <section className="relative bg-[#0F172A] overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 60% at 50% 110%, rgba(255,94,26,0.18), transparent 70%)" }} />
      <div className="container-nb relative py-24 md:py-32 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight text-[#F8FAFC] max-w-2xl mx-auto">
            Let's talk about your<br />next critical hire.
          </h2>
          <Link to="/contact" data-testid="home-final-cta" className="btn-primary mt-10 inline-flex">
            Let's Talk <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Home;
